import React, { useState, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { uniq, isEmpty, isNil } from 'ramda';
import { Link } from 'react-router-dom';

// store
import { storeGet, useStoreValue } from '../../../../store';

// api
import TicketService from '../../../../service/ticket-service';

// tools
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';

// components
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import ProperBandoLabel from '../../../../components/ProperBandoLabel';

import translationStrings from '../../../../translationStringsForComponents';
import { ticketsPriority, ticketsPriorityOrder } from '../../../../configData';


const ListTickets = () => {
    const [items, setItems] = useState(null);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(false);
    const [, setStatuses] = useState([]);

    useEffect(() => {
        setLoading(true);
        const getRole = storeGet('getRole');

        if ('ROLE_CONFIDI' !== getRole) {
            TicketService.getTickets(getCallback, errGetCallbacks);
        } else {
            const userData = storeGet('userData');
            TicketService.getTicketsSupplier(userData.id, getCallback, errGetCallbacks);
        }
    }, []);

    const getCallback = (resp) => {
        if (resp.status === 'SUCCESS' && resp.data) {
            setItems(getFormattedData(resp.data));
            setStatuses(uniq(resp.data.map(o => o.status)))
            initFilters();
        }
        setLoading(false);
    }

    const errGetCallbacks = (resp) => {
        set404FromErrorResponse(resp);
        setLoading(false);
    }

    const getFormattedData = (data) => {
        return [...(data || [])].map((d) => {
            d.updatedDate = new Date(d.updatedDate);
            d.createdDate = new Date(d.createdDate);

            return d;
        });
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            title: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            companyName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            createdDate: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
            },
            updatedDate: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
            },
            status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
        });
    };

    const renderHeader = () => {
        return (
            <div className="appTableHeader">
                <Button type="button" icon="pi pi-filter-slash" label={__('Pulisci', 'htr')} outlined
                        onClick={clearFilter}/>
            </div>
        );
    };

    const dateStartBodyTemplate = (rowData) => {
        return formatDate(rowData.createdDate);
    };

    const progressTemplate = (rowData) => {
        return `${rowData.documentRecieved} / ${rowData.totalRequiredDocument}`;
    };

    const dateFilterTemplate = (options) => {
        return <Calendar value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999"/>;
    };

    const statusBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.status}/>;
    };

    const priorityBodyTemplate = (rowData) => {
        return ticketsPriority[rowData.priority];
    };

    const prioritySortFunction = (event) => {
        const data = [...event.data];
        data.sort((a, b) => {
            const orderA = ticketsPriorityOrder[a.priority] ?? 999;
            const orderB = ticketsPriorityOrder[b.priority] ?? 999;
            return event.order * (orderA - orderB);
        });
        return data;
    };

    const actionsBodyTemplate = (rowData) => {
        return <div className="appPageSection__tableActions">
            <Link to={`/assistenza/ticket/${rowData.id}`}>
                <Button severity="info" label={__('Mostra', 'htr')} icon="pi pi-arrow-right" size="small"
                        iconPos="right"/>
            </Link></div>
    }

    const header = renderHeader();

    return (
        <div className="appPageSection__table">
            <DataTable value={items} paginator showGridlines rows={5} loading={loading} dataKey="id"
                       filters={filters} stripedRows removableSort
                       header={header}
                       emptyMessage={translationStrings.emptyMessage}
                       onFilter={(e) => setFilters(e.filters)}>
                <Column field="id" header={__('ID', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il ID', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="companyName" header={__('Fornitore', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il nome', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="title" header={__('Oggetto', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il oggetto', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="priority" header={__('Priorità', 'htr')}
                        filter sortable sortFunction={prioritySortFunction}
                        filterPlaceholder={__('Cerca la priorità', 'htr')}
                        style={{ minWidth: '7rem' }} body={priorityBodyTemplate}/>
                <Column header={__('Data creazione', 'htr')} filterField="createdDate" dataType="date"
                        style={{ minWidth: '8rem' }}
                        body={dateStartBodyTemplate} filter filterElement={dateFilterTemplate}/>
                <Column field="status" header={__('Stato', 'htr')}
                        style={{ minWidth: '7rem' }} body={statusBodyTemplate} />
                <Column header={__('Azioni', 'htr')}
                        body={actionsBodyTemplate}/>
            </DataTable>
        </div>
    )
}

export default ListTickets;